import React, { useMemo } from "react";
import Select, { components, ControlProps } from "react-select";
import CreatableSelect from "react-select/creatable";
import { useField } from "formik";
import { Img } from "../Img/index";
interface DropdownProps {
  label?: string;
  required?: boolean;
  options: { value: string; label: string }[];
  name: string;
  placeHolder: string;
  isMulti?: boolean;
  labelClassNames?: string;
  customHeight?: string;
  leftIcon?: string;
  controlCustomStyle?: any;
  placeholderCustomStyle?: any;
  disable?: boolean;
  isCreatable?: boolean;
}

function hasMissingElements(options, values) {
  return values?.some(item => !options?.some(entry => entry.value.toLowerCase() === item.toLowerCase()));
}

function addMissingElements(options, values) {
  values.forEach(item => {
    let isPresent = options?.some(entry => entry.value.toLowerCase() === item.toLowerCase());

    if (!isPresent) {
      options.push({ value: item, label: item });
    }
  });

  return options;
}

const Dropdown: React.FC<DropdownProps> = ({
  label,
  options,
  name,
  placeHolder,
  required,
  isMulti = false,
  labelClassNames,
  customHeight = "37px",
  controlCustomStyle,
  placeholderCustomStyle,
  disable,
  isCreatable,
  leftIcon,
}) => {
  const [field, meta, helpers] = useField(name);
  const { setValue } = helpers;
  const [customOption, setCustomOption] = React.useState([]);

  const isError = meta.touched && meta.error;

  useMemo(() => {
    if (options?.length) {
      if (isMulti) {
        if (hasMissingElements(options, field.value) && !!field.value?.length) {
          setCustomOption(addMissingElements(options, field.value));
        } else {
          setCustomOption([...options]);
        }
      } else {
        if (!options.find((option) => option.value === field.value) && !!field.value?.length) {
          const newOption = { value: field.value, label: field.value };
          setCustomOption([...options, newOption]);
        } else {
          setCustomOption([...options]);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options?.length]);

  const handleChange = (selectedOptions: any) => {
    if (isMulti) {
      const values = selectedOptions.map((option: any) => option.value);
      setValue(values);
    } else {
      const selectedValue = selectedOptions?.value || "";
      setValue(selectedValue);
    }
  };

  const handleCreate = (inputValue: string) => {
    const newOption = { value: inputValue, label: inputValue };
    setCustomOption(customOption => [...customOption, newOption]);
    if (isMulti) {
      setValue([...field.value, inputValue]); // Set the new value
    } else {
      setValue(inputValue); // Set the new value
    }
  };

  const getValue = () => {
    if (isMulti) {
      return customOption.filter((option) => field?.value?.includes(option.value));
    } else {
      return customOption.find((option) => option?.value === field?.value);
    }
  };

  const CustomControl = (props: ControlProps<any, boolean>) => {
    const { children, innerRef, innerProps } = props; // Destructure required props

    return (
      <components.Control {...props} innerRef={innerRef} innerProps={innerProps}>
        {!!leftIcon && <div className="px-2">
          <Img src={leftIcon} className="w-5 h-5" />
        </div>}
        {children}
      </components.Control>
    );
  };

  return !!customOption?.length ? (
    <div className="flex flex-col gap-1 w-full">
      {Boolean(!!label || isError) && <div className="flex items-center justify-between">
        {!!label && (
          <label
            className={`font-manropeSB text-[#4a5568] text-[1.8vh] ${labelClassNames || ""}`}
          >
            {label} {!!required && <><span className="text-red-700">*</span></>}
          </label>)}
        <span>
          {isError && (
            <div className="text-red-600 text-[2.2vh]">
              {Array.isArray(meta.error) ? meta.error.join(", ") : meta.error}
            </div>)}
        </span>
      </div>}
      <div className="flex items-center gap-2">
        <div className="w-full rounded-lg">
          {isCreatable ? <CreatableSelect
            isDisabled={disable}
            menuPlacement="top"
            options={customOption}
            isMulti={isMulti}
            closeMenuOnSelect={!isMulti}
            onChange={handleChange}
            onCreateOption={handleCreate}
            placeholder={placeHolder}
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              control: (base, state) => ({
                ...base,
                borderRadius: "0.7rem",
                height: isMulti ? "auto" : "7vh",
                minHeight: "7vh",
                padding: `0 calc(${customHeight} / 8)`,
                flexWrap: isMulti ? "wrap" : "nowrap",
                border: isError ? "1px solid red" : "1px solid #CECECE",
                boxShadow: "none", // REMOVE the blue focus shadow
                borderColor: isError ? "red" : "#CECECE", // Prevent blue border on focus
                ...(controlCustomStyle || {}),
              }),
              placeholder: (base) => ({
                ...base,
                fontSize: "1.8vh",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden",
                ...(placeholderCustomStyle || {}),
              }),
              menu: (base) => ({
                ...base,
                maxHeight: "400px",
                overflowY: "auto",
                scrollbarWidth: "thin",
                msOverflowStyle: "auto",
                zIndex: 999,
                background: "rgba(255, 255, 255, 0.8)", // Semi-transparent white background
                border: "2px solid rgba(0, 0, 0, 0.1)", // Light border
                borderRadius: "8px",
                backdropFilter: "blur(20px)", // Blur effect for glassmorphism
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)", // Subtle shadow
                "::-webkit-scrollbar": {
                  width: "6px",
                  height: "6px",
                },
                "::-webkit-scrollbar-thumb": {
                  background: "rgba(0, 0, 0, 0.2)", // Scrollbar thumb color
                  borderRadius: "10px",
                },
                "::-webkit-scrollbar-track": {
                  background: "rgba(0, 0, 0, 0.05)", // Scrollbar track color
                  borderRadius: "10px",
                },
              }),
              option: (base, { isFocused, isSelected }) => ({
                ...base,
                textAlign: "center",
                background: isSelected
                  ? "rgba(0, 123, 255, 0.8)" // Blue background for selected option
                  : isFocused
                    ? "rgba(0, 123, 255, 0.2)" // Light blue background on hover
                    : "transparent",
                color: isSelected ? "#fff" : isFocused ? "#000" : "#333", // Text color
                cursor: "pointer",
                backdropFilter: isFocused ? "blur(5px)" : "none", // Blur effect on hover
                transition: "background 0.3s ease, color 0.3s ease", // Smooth transition
              }),
            }}
            value={getValue()}
            isClearable
          /> : <Select
            isDisabled={disable}
            menuPlacement="top"
            options={customOption}
            isMulti={isMulti}
            closeMenuOnSelect={!isMulti}
            onChange={handleChange}
            // onCreateOption={handleCreate}
            placeholder={placeHolder}
            components={{ Control: CustomControl }} // Using the fixed Custom Control
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              control: (base, state) => ({
                ...base,
                borderRadius: "0.7rem",
                height: isMulti ? "auto" : "7vh",
                minHeight: "7vh",
                padding: `0 calc(${customHeight} / 8)`,
                flexWrap: isMulti ? "wrap" : "nowrap",
                border: isError ? "1px solid red" : "1px solid #CECECE",
                boxShadow: "none", // Remove default react-select focus shadow
                borderColor: isError ? "red" : "#CECECE", // Maintain custom border color
                ...(controlCustomStyle || {}),
              }),
              placeholder: (base) => ({
                ...base,
                fontSize: "1.8vh",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden",
                ...(placeholderCustomStyle || {}),
              }),
              menu: (base) => ({
                ...base,
                maxHeight: "400px",
                overflowY: "auto",
                scrollbarWidth: "thin",
                msOverflowStyle: "auto",
                zIndex: 999,
                background: "rgba(255, 255, 255, 0.8)", // Semi-transparent white background
                border: "2px solid rgba(0, 0, 0, 0.1)", // Light border
                borderRadius: "8px",
                backdropFilter: "blur(20px)", // Blur effect for glassmorphism
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)", // Subtle shadow
                "::-webkit-scrollbar": {
                  width: "6px",
                  height: "6px",
                },
                "::-webkit-scrollbar-thumb": {
                  background: "rgba(0, 0, 0, 0.2)", // Scrollbar thumb color
                  borderRadius: "10px",
                },
                "::-webkit-scrollbar-track": {
                  background: "rgba(0, 0, 0, 0.05)", // Scrollbar track color
                  borderRadius: "10px",
                },
              }),
              option: (base, { isFocused, isSelected }) => ({
                ...base,
                textAlign: "center",
                background: isSelected
                  ? "rgba(0, 123, 255, 0.8)" // Blue background for selected option
                  : isFocused
                    ? "rgba(0, 123, 255, 0.2)" // Light blue background on hover
                    : "transparent",
                color: isSelected ? "#fff" : isFocused ? "#000" : "#333", // Text color
                cursor: "pointer",
                backdropFilter: isFocused ? "blur(5px)" : "none", // Blur effect on hover
                transition: "background 0.3s ease, color 0.3s ease", // Smooth transition
              }),
            }}
            value={getValue()}
            isClearable
          />}
        </div>
      </div>
    </div>
  ) : <></>;
};

export default Dropdown;